"use client";

import { useEffect, useRef } from "react";
import Section, { SectionHeader } from "@/components/Section";
import CodeBlock from "@/components/CodeBlock";
import Link from "next/link";
import { IconArrowRight } from "@/components/icons";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const REACT_CODE = `npm install @growth-rail/react

import { GrowthRailProvider } from '@growth-rail/react';

function App() {
  return (
    <GrowthRailProvider projectSecretKey="sk_live_xxx">
      <YourApp />
    </GrowthRailProvider>
  );
}`;

const RN_CODE = `npm install @growth-rail/react-native

import { GrowthRailProvider } from '@growth-rail/react-native';

function App() {
  return (
    <GrowthRailProvider projectSecretKey="sk_live_xxx">
      <YourApp />
    </GrowthRailProvider>
  );
}`;

const JS_CODE = `npm install @growth-rail/core

import { GrowthRail } from '@growth-rail/core';

GrowthRail.init({
  projectSecretKey: 'sk_live_xxx'
});`;

const SWIFT_CODE = `import GrowthRail
import SwiftUI

GrowthRail.configure(
  GrowthRailConfiguration(
    projectSecretKey: "YOUR_MOBILE_KEY",
    appearance: .dark
  )
)`;

const KOTLIN_CODE = `import dev.growthrail.sdk.GrowthRail
import dev.growthrail.sdk.GrowthRailConfiguration

GrowthRail.initialize(
  this,
  GrowthRailConfiguration(
    projectSecretKey = "YOUR_MOBILE_KEY"
  )
)`;

const API_CODE = `POST /api/v1/projects/{projectId}/referrals/link
Authorization: Bearer grak_live_xxxx

{
  "referrerId": "user_123",
  "rewardEventName": "purchase"
}`;

const SHOWCASE_TABS = [
  { id: "react", label: "React", code: REACT_CODE },
  { id: "rn", label: "React Native", code: RN_CODE },
  { id: "js", label: "JavaScript", code: JS_CODE },
  { id: "swift", label: "Swift", code: SWIFT_CODE },
  { id: "kotlin", label: "Kotlin", code: KOTLIN_CODE },
  { id: "api", label: "REST API", code: API_CODE },
];

export default function SdkShowcase() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    gsap.registerPlugin(ScrollTrigger);
    if (!containerRef.current) return;

    const ctx = gsap.context(() => {
      gsap.from(".anim-code-block", {
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 75%",
        },
        x: -50,
        duration: 0.8,
        ease: "power3.out",
      });
      
      gsap.from(".anim-text-block", {
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 75%",
        },
        x: 50,
        duration: 0.8,
        delay: 0.1,
        ease: "power3.out",
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section id="developers" className="overflow-clip">
      <div ref={containerRef} className="grid items-center gap-12 lg:grid-cols-[1.3fr_1fr]">
        
        {/* Left Side: Code Window */}
        <div className="anim-code-block relative w-full min-w-0 max-w-2xl mx-auto lg:mx-0 lg:order-1 order-2">
          <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-copper-500/15 via-transparent to-transparent opacity-60 blur-2xl transform-gpu will-change-[filter,transform]" />
          <CodeBlock
            filename="integration"
            autoPlay={true}
            className="min-h-[380px]"
            tabs={SHOWCASE_TABS}
          />
        </div>

        {/* Right Side: Text & Button */}
        <div className="anim-text-block flex flex-col items-start lg:order-2 order-1 text-left">
          <SectionHeader
            eyebrow="Developer-Friendly"
            title={
              <>
                Launch in minutes with our <span className="text-gradient-warm">Referral SDK & API</span>
              </>
            }
            description="Drop-in UI components, React hooks, and native SDKs for React Native, iOS (Swift), and Android (Kotlin) let your team integrate a referral program with just a few lines of code."
            align="left"
            className="!mx-0"
          />

          <div className="mt-8">
            <Link
              href="https://docs.growthrail.dev"
              className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3 text-sm font-medium text-white transition-all hover:bg-white/10 hover:border-copper-500/30 hover:shadow-[0_0_20px_-5px_rgba(235,160,113,0.3)]"
            >
              Explore Developer Docs
              <IconArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

      </div>
    </Section>
  );
}
